import React, { useState } from 'react';

// Public contact endpoint (wolftechContactApi — POST /contact, no auth).
const CONTACT_API_URL = 'https://o0f9oz9qq6.execute-api.eu-west-2.amazonaws.com/home/contact';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', company: '', project: '', website: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    setErrorMsg('');
    try {
      const res = await fetch(CONTACT_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          company: form.company,
          message: form.project,
          website: form.website, // honeypot — humans leave this empty
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Something went wrong. Please try again.');
      }
      setStatus('success');
      setForm({ name: '', email: '', company: '', project: '', website: '' });
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Something went wrong. Please email us directly at support@wolftechnologies.co.uk.');
    }
  };

  return (
    <section id="contact" className="contact section">
      <div className="container">
        <div className="section__header">
          <span className="section__tag">Get In Touch</span>
          <h2 className="section__title">
            Ready to Build Something
            <span className="gradient-text"> Extraordinary?</span>
          </h2>
          <p className="section__subtitle">
            Whether you have a fully formed app idea or just the spark of an inspiration,
            we'd love to hear from you. Let's discuss how AI and AWS can bring your vision to life.
          </p>
        </div>

        <div className="contact__grid">
          <div className="contact__info">
            <div className="contact__info-card">
              <div className="contact__info-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
              </div>
              <div>
                <h4>Email Us</h4>
                <p>support@wolftechnologies.co.uk</p>
              </div>
            </div>

            <div className="contact__info-card">
              <div className="contact__info-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                </svg>
              </div>
              <div>
                <h4>Location</h4>
                <p>United Kingdom</p>
              </div>
            </div>

            <div className="contact__info-card">
              <div className="contact__info-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h4>Response Time</h4>
                <p>Within 24 hours</p>
              </div>
            </div>

            <div className="contact__platforms">
              <h4>We Build For</h4>
              <div className="contact__platform-badges">
                <span className="contact__platform-badge">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  iOS / App Store
                </span>
                <span className="contact__platform-badge">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                    <path d="M17.523 15.341l1.412-2.453a.504.504 0 00-.184-.687.505.505 0 00-.688.184l-1.43 2.486a10.252 10.252 0 00-4.634-1.098 10.252 10.252 0 00-4.633 1.098l-1.43-2.486a.505.505 0 00-.688-.184.504.504 0 00-.184.687l1.412 2.453C3.186 17.173 1 20.201 1 23.725h22c0-3.524-2.186-6.552-5.477-8.384zM7.5 21.225c-.553 0-1-.448-1-1s.447-1 1-1 1 .448 1 1-.447 1-1 1zm9 0c-.553 0-1-.448-1-1s.447-1 1-1 1 .448 1 1-.447 1-1 1z"/>
                  </svg>
                  Android / Google Play
                </span>
              </div>
            </div>
          </div>

          <form className="contact__form" onSubmit={handleSubmit}>
            <div className="contact__form-row">
              <div className="contact__form-group">
                <label htmlFor="name">Full Name</label>
                <input type="text" id="name" name="name" placeholder="John Smith" required minLength={2} maxLength={200} value={form.name} onChange={handleChange} disabled={status === 'sending'} />
              </div>
              <div className="contact__form-group">
                <label htmlFor="email">Email Address</label>
                <input type="email" id="email" name="email" placeholder="john@example.com" required maxLength={320} value={form.email} onChange={handleChange} disabled={status === 'sending'} />
              </div>
            </div>
            <div className="contact__form-group">
              <label htmlFor="company">Company (Optional)</label>
              <input type="text" id="company" name="company" placeholder="Your company" maxLength={200} value={form.company} onChange={handleChange} disabled={status === 'sending'} />
            </div>
            {/* Honeypot — hidden from humans, bots auto-fill it. Leave empty. */}
            <div className="contact__form-hp" aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input type="text" id="website" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={handleChange} />
            </div>
            <div className="contact__form-group">
              <label htmlFor="project">Tell Us About Your Project</label>
              <textarea id="project" name="project" rows="5" placeholder="Describe your app idea, target audience, and any specific requirements..." required minLength={10} maxLength={5000} value={form.project} onChange={handleChange} disabled={status === 'sending'} />
            </div>
            {status === 'success' && (
              <div className="contact__form-status contact__form-status--success" role="status">
                Thanks — your message is on its way. We'll get back to you within 24 hours.
              </div>
            )}
            {status === 'error' && (
              <div className="contact__form-status contact__form-status--error" role="alert">
                {errorMsg}
              </div>
            )}
            <button type="submit" className="btn btn--primary btn--large contact__form-submit" disabled={status === 'sending'}>
              {status === 'sending' ? 'Sending…' : 'Send Message'}
              {status !== 'sending' && (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M4 10h12m0 0l-4-4m4 4l-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
